import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { useNavigate } from 'react-router-dom';
import Recorder from "recorder-js";
import EnergyVisualizer from "../../User/Questions/ER";
import { EmployerLayout } from "../JobPostPricing/EmployerLayout";
import { getApiUrl } from '../../../config/api';
import { Button } from '@/Components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '@/Components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/Components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/Components/ui/popover';
import { Alert, AlertDescription, Progress } from '@/Components/ui/feedback';
import { Badge } from '@/Components/ui/badge';
import { Heading, Text } from '@/Components/ui/typography';
import { Mic, Volume2, Loader2, CheckCircle2, Sparkles, MessageSquare, Headphones, X, Radio, ChevronDown, Lightbulb, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { getColor } from '@/config/theme';

const questions = [
  "Hello, I hope you are doing well today. I would help you to create a job description. I will ask a few key questions to understand what you are looking for.",
  "What is the title of the role you are hiring for?",
  "What level of experience are you looking for in this role?",
  "Is there a salary range or budget you would like to define for this position?",
  "How would you describe this role to a potential candidate in a few sentences?",
  "What are the primary responsibilities associated with this position?",
  "Which skills or qualifications are essential for success in this role?",
  "Where will this role be based? (Remote, hybrid, or a specific location)",
  "Thank you. I have created the job description based on your responses. Please review and save it when ready."
];

export default function JdAudioAI() {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const transcriptText = useRef("");
  const audioRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const lastTranscriptTimeRef = useRef(Date.now());
  const { transcript, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();
  const [showInstructions, setShowInstructions] = useState(true);
  const [micStatus, setMicStatus] = useState("idle");
  const [micMessage, setMicMessage] = useState("");
  const [speakerStatus, setSpeakerStatus] = useState("idle");
  const recorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(true);
  const [contextualTipsOpen, setContextualTipsOpen] = useState(false);

  // Contextual tips based on question index
  const getContextualTips = () => {
    const tipsByQuestion = {
      0: [
        { icon: MessageSquare, text: "Be specific: 'Senior Frontend Developer' is better than 'Developer'" },
        { icon: CheckCircle2, text: "Include level if relevant: Junior, Mid-level, Senior, Lead, etc." }
      ],
      1: [
        { icon: CheckCircle2, text: "Specify years: '3-5 years' or '5+ years experience'" },
        { icon: MessageSquare, text: "Mention industry experience if relevant" }
      ],
      2: [
        { icon: Sparkles, text: "Include currency and range: '₹10-15 lakhs' or 'Competitive salary'" },
        { icon: CheckCircle2, text: "Mention if negotiable or includes benefits" }
      ],
      3: [
        { icon: MessageSquare, text: "Keep it concise - 2-3 sentences summarizing the role" },
        { icon: CheckCircle2, text: "Focus on what makes this role unique" }
      ],
      4: [
        { icon: CheckCircle2, text: "List 3-5 main responsibilities" },
        { icon: MessageSquare, text: "Use action verbs: 'Develop', 'Manage', 'Collaborate'" }
      ],
      5: [
        { icon: Sparkles, text: "Be specific: 'React, TypeScript, Node.js' not just 'JavaScript'" },
        { icon: CheckCircle2, text: "Separate must-have from nice-to-have skills" }
      ],
      6: [
        { icon: MessageSquare, text: "Specify: 'Remote', 'Hybrid', 'On-site', or 'Flexible'" },
        { icon: CheckCircle2, text: "Include city/region if location-specific" }
      ]
    };
    return tipsByQuestion[currentIndex] || [
      { icon: CheckCircle2, text: "Speak clearly and naturally" },
      { icon: MessageSquare, text: "Take your time to provide complete answers" }
    ];
  };

  // useEffect(() => {
  //   if (!browserSupportsSpeechRecognition) {
  //     alert("Your browser doesn't support speech recognition.");
  //     return;
  //   }
  //   handleFlow();
  // }, []);

  useEffect(() => {
    if (transcript && transcript.trim().length > 0) {
      transcriptText.current = transcript;
      lastTranscriptTimeRef.current = Date.now();
    }
  }, [transcript]);

  useEffect(() => {
    if (currentIndex !== 0) {
      handleFlow();
    }
  }, [currentIndex]);

  useEffect(() => {
    if (!browserSupportsSpeechRecognition) {
      toast.error("Your browser doesn't support speech recognition.");
      return;
    }
  
    if (!showInstructions) {
      handleFlow(); // only start after tests
    }
  }, [showInstructions]);

  const handleMicTest = async () => {
    setMicStatus("Testing");
    setMicMessage("");

    const recorder = await initializeRecorder();
    if (!recorder) {
      setMicStatus("Error");
      setMicMessage("Failed to initialize microphone.");
      return;
    }

    try {
      recorder.start();
      setTimeout(async () => {
        const { blob } = await recorder.stop();

        // ✅ Create URL and save to state
        const audioURL = URL.createObjectURL(blob);

        // Send to backend as before
        const formData = new FormData();
        formData.append("audio", blob);

        setMicStatus("Tested");
        setMicMessage("Microphone test completed successfully.");
      }, 5000);
    } catch (error) {
      console.error("Microphone access denied:", error);
      setMicStatus("Error");
      setMicMessage("Microphone access denied.");
    }
  };

  const handleSpeakerTest = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      gainNode.gain.setValueAtTime(1, audioContext.currentTime);
      oscillator.start();
      setSpeakerStatus("Testing");

      setTimeout(() => {
        oscillator.stop();
        audioContext.close();
        setSpeakerStatus("Tested");
      }, 1000);
    } catch (error) {
      console.error("Error generating beep sound:", error);
      setSpeakerStatus("Error");
    }
  };

  const initializeRecorder = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const recorder = new Recorder(audioContext);
        recorder.init(stream);
  
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        source.connect(analyser);
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteTimeDomainData(dataArray);
  
        audioContextRef.current = audioContext;
        recorderRef.current = recorder;
        return recorder;
      } catch (error) {
        console.error("Error initializing Recorder.js:", error);
        return null;
      }
  };

  const handleFlow = async () => {
    if (currentIndex >= questions.length - 1) {
      try {
        // Only include actual questions (indices 1-7), skip greeting (0) and farewell (8)
        const prompt = questions
          .slice(1, questions.length - 1)
          .map((q, i) => `Q: ${q}\nA: ${answers[i + 1] || "No answer"}`)
          .join("\n\n");

        const res = await axios.post(getApiUrl("/api/generate_jd_ai"), {
          prompt,
          company_id:    localStorage.getItem("companyAccountId"),
          company_email: localStorage.getItem("employer_email"),
          company_name:  localStorage.getItem("company_name"),
        });
        const jd = res.data.job_details;

        if (!jd) {
          toast.error("Failed to generate job description. Please try again.");
          return;
        }

        const thankYouMessage = await axios.post(getApiUrl("/api/generate_audio"), {
          text: "Thank you. The job description has been successfully created. Please review and save it.",
        }, { responseType: "blob" });
        setIsSpeaking(true);
        const url = URL.createObjectURL(thankYouMessage.data);
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.play();
        audio.onended = () => {
          setIsSpeaking(false);
          localStorage.setItem('generatedJdData', JSON.stringify(jd));
          navigate("/hirewithus/postjob");
        };
      } catch (err) {
        console.error("JD generation failed:", err);
        const status = err?.response?.status;
        const serverMsg = err?.response?.data?.error;
        if ([402, 403].includes(status) && serverMsg) {
          // Plan/limit refusal: the server sends a customer-ready sentence.
          toast.error(serverMsg, { duration: 12000 });
        } else {
          toast.error("Failed to generate job description. Please try again.");
        }
      }
      return;
    }
    try {
      const res = await axios.post(getApiUrl("/api/generate_audio"), {
        text: questions[currentIndex],
      }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      setIsSpeaking(true);
      audioRef.current = audio;
      audio.play();
      audio.onended = () => {
        startListening();
        setIsSpeaking(false);
      };
    } catch (err) {
      console.error("Audio generation failed:", err);
      toast.error("Failed to play question audio. Please try again.");
    }
  };

  const startListening = () => {
    setIsRecording(true);
    resetTranscript();
    transcriptText.current = "";
    SpeechRecognition.startListening({ continuous: true });
    lastTranscriptTimeRef.current = Date.now();
    const answerStartedAt = Date.now();
    const MAX_ANSWER_MS = 90 * 1000; // hard per-answer cap (cost + UX safeguard)
    if (silenceTimerRef.current) clearInterval(silenceTimerRef.current);
    silenceTimerRef.current = setInterval(() => {
      const now = Date.now();
      // Auto-stop after a short pause in speech.
      if (now - lastTranscriptTimeRef.current > 3000) {
        stopListening();
        return;
      }
      // Hard cap: never let a single answer record longer than MAX_ANSWER_MS.
      if (now - answerStartedAt > MAX_ANSWER_MS) {
        stopListening();
      }
    }, 1000);
  };

  const stopListening = () => {
    setIsRecording(false);
    SpeechRecognition.stopListening();
    clearInterval(silenceTimerRef.current);
    const updated = [...answers];
    updated[currentIndex] = transcriptText.current.trim() || "No answer";
    setAnswers(updated);
    transcriptText.current = "";
    const acknowledgementPhrases = [
      "Thank you for sharing.",
      "Got it, thank you.",
      "That's clear.",
      "Understood.",
      "Thanks, I have got that.",
      "Perfect, thank you.",
      "Thanks for clarifying."
    ];
    const randomPhrase = acknowledgementPhrases[Math.floor(Math.random() * acknowledgementPhrases.length)];
    const playAcknowledgement = async () => {
      const ackRes = await axios.post(getApiUrl("/api/generate_audio"), {
        text: randomPhrase,
      }, { responseType: "blob" });
      const ackUrl = URL.createObjectURL(ackRes.data);
      const ackAudio = new Audio(ackUrl);
      audioRef.current = ackAudio;
      ackAudio.play();
      ackAudio.onended = () => {
        setCurrentIndex(prev => prev + 1);
      };
    };
    playAcknowledgement();
  };

  // const stopAllProcesses = () => {
  //   setStatus("idle");
  //   SpeechRecognition.stopListening();
  //   if (silenceTimerRef.current) {
  //     clearInterval(silenceTimerRef.current);
  //     silenceTimerRef.current = null;
  //   }
  //   if (audioRef.current) {
  //     audioRef.current.pause();
  //     audioRef.current.currentTime = 0;
  //   }
  //   transcriptText.current = "";
  // };
  
  return (
    <EmployerLayout>
      <div className="flex flex-col w-full gap-6">
        {/* Back Navigation */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/hirewithus/postjob')}
          className="w-fit"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Post Job Options
        </Button>
        
        <div className="flex flex-col w-full gap-5">

                {/* Audio Setup Modal */}
                {showInstructions && (
                  <div 
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
                    onClick={(e) => {
                      // Close modal when clicking outside the card
                      if (e.target === e.currentTarget) {
                        navigate('/hirewithus/postjob');
                      }
                    }}
                  >
                    <Card 
                      className="w-full max-w-[500px] mx-4 border shadow-lg relative"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <CardHeader className="text-center space-y-2 pb-4 relative">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-4 right-4 h-8 w-8 rounded-full"
                          onClick={() => navigate('/hirewithus/postjob')}
                        >
                          <X className="h-4 w-4" />
                          <span className="sr-only">Close</span>
                        </Button>
                        <div className="flex items-center justify-center gap-2.5 mb-1">
                          <div className="p-1.5 rounded-md bg-primary/10">
                            <Mic className="h-5 w-5 text-primary" />
                          </div>
                          <CardTitle className="text-2xl font-semibold">
                            Audio Setup
                          </CardTitle>
                        </div>
                        <CardDescription>
                          Please test your microphone and speaker before starting.
                        </CardDescription>
                      </CardHeader>

                      <CardContent className="space-y-5">
                        {/* Microphone Test */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Text size="sm" weight="medium" className="text-foreground">
                              Microphone Test
                            </Text>
                            {micStatus === "Tested" && (
                              <Badge variant="outline" className="gap-1 text-xs">
                                <CheckCircle2 className="h-3 w-3 text-primary" />
                                Ready
                              </Badge>
                            )}
                          </div>
                          <Button
                            onClick={handleMicTest}
                            variant="outline"
                            disabled={micStatus === "Testing"}
                            className={cn(
                              "w-full gap-2 transition-all",
                              micStatus === "Tested" && "border-primary/50 bg-primary/5"
                            )}
                            size="lg"
                          >
                            {micStatus === "Testing" ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Testing Microphone...
                              </>
                            ) : micStatus === "Tested" ? (
                              <>
                                <CheckCircle2 className="h-4 w-4 text-primary" />
                                Microphone Ready
                              </>
                            ) : (
                              <>
                                <Mic className="h-4 w-4" />
                                Test Microphone
                              </>
                            )}
                          </Button>
                          {micMessage && (
                            <Alert 
                              variant={micStatus === "Error" ? "destructive" : "success"}
                              className="py-2"
                            >
                              <AlertDescription className="text-sm">
                                {micMessage}
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>

                        {/* Divider */}
                        <div className="border-t border-border"></div>

                        {/* Speaker Test */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Text size="sm" weight="medium" className="text-foreground">
                              Speaker Test
                            </Text>
                            {speakerStatus === "Tested" && (
                              <Badge variant="outline" className="gap-1 text-xs">
                                <CheckCircle2 className="h-3 w-3 text-primary" />
                                Ready
                              </Badge>
                            )}
                          </div>
                          <Button
                            onClick={handleSpeakerTest}
                            variant="outline"
                            disabled={speakerStatus === "Testing"}
                            className={cn(
                              "w-full gap-2 transition-all",
                              speakerStatus === "Tested" && "border-primary/50 bg-primary/5"
                            )}
                            size="lg"
                          >
                            {speakerStatus === "Testing" ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Testing Speaker...
                              </>
                            ) : speakerStatus === "Tested" ? (
                              <>
                                <CheckCircle2 className="h-4 w-4 text-primary" />
                                Speaker Ready
                              </>
                            ) : (
                              <>
                                <Volume2 className="h-4 w-4" />
                                Test Speaker
                              </>
                            )}
                          </Button>
                          {speakerStatus === "Tested" && (
                            <Alert variant="success" className="py-2">
                              <AlertDescription className="text-sm">
                                Speaker is working!
                              </AlertDescription>
                            </Alert>
                          )}
                          {speakerStatus === "Error" && (
                            <Alert variant="destructive" className="py-2">
                              <AlertDescription className="text-sm">
                                Speaker test failed. Please check your audio settings.
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>
                      </CardContent>

                      <CardFooter>
                        <Button
                          onClick={() => setShowInstructions(false)}
                          disabled={!(micStatus === "Tested" && speakerStatus === "Tested")}
                          className="w-full gap-2"
                          size="lg"
                          style={{
                            backgroundColor: getColor('primary'),
                            color: getColor('primaryForeground'),
                            borderColor: getColor('primary')
                          }}
                        >
                          <Sparkles className="h-4 w-4" />
                          Generate JD
                        </Button>
                      </CardFooter>
                    </Card>
                  </div>
                )}

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                  {/* AI Interview Section */}
                  <Card className="lg:col-span-2 border shadow-sm">
                    <CardHeader className="space-y-3 pb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 rounded-md bg-primary/10">
                          <MessageSquare className="h-5 w-5 text-primary" />
                        </div>
                        <Heading level="h4">AI Interviewer: Job Description Creation</Heading>
                      </div>
                      
                      {/* Progress Indicator */}
                      <div className="space-y-2 pt-2">
                        <div className="flex items-center justify-between text-sm">
                          <Text variant="muted" size="sm" className="font-medium">
                            Question {currentIndex + 1} of {questions.length - 1}
                          </Text>
                          <Text variant="muted" size="sm">
                            {(() => {
                              const totalQuestions = questions.length - 1; // Exclude thank you message
                              const completedAnswers = answers.filter(a => a && a.trim() !== "" && a !== "No answer").length;
                              const progress = Math.round((completedAnswers / totalQuestions) * 100);
                              return completedAnswers > 0 ? `${progress}% Complete` : "Starting...";
                            })()}
                          </Text>
                        </div>
                        <Progress 
                          value={(() => {
                            const totalQuestions = questions.length - 1;
                            const completedAnswers = answers.filter(a => a && a.trim() !== "" && a !== "No answer").length;
                            return (completedAnswers / totalQuestions) * 100;
                          })()} 
                          className="h-2"
                        />
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-6">
                      {/* Avatar Section */}
                      <div className="flex flex-col items-center justify-center space-y-4 py-4">
                        <div className={cn(
                          "relative w-36 h-36 rounded-full bg-primary/10 flex items-center justify-center shadow-md border border-primary/20 transition-all duration-300",
                          isRecording && "ring-4 ring-accent/30 ring-offset-2 animate-pulse",
                          isSpeaking && "ring-4 ring-primary/30 ring-offset-2"
                        )}>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <EnergyVisualizer isSpeaking={isSpeaking} isRecording={isRecording} />
                          </div>
                          {isRecording && (
                            <div className="absolute -top-2 -right-2">
                              <div className="relative flex h-6 w-6">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-6 w-6 bg-accent"></span>
                              </div>
                            </div>
                          )}
                        </div>
                        <Badge 
                          variant={isSpeaking ? "default" : isRecording ? "secondary" : "outline"}
                          className={cn(
                            "gap-1.5 text-sm px-4 py-1.5 transition-all duration-200",
                            isRecording && "animate-pulse"
                          )}
                          style={
                            isSpeaking 
                              ? {
                                  backgroundColor: getColor('primary'),
                                  color: getColor('primaryForeground')
                                }
                              : isRecording
                              ? {
                                  backgroundColor: getColor('accent'),
                                  color: getColor('accentForeground')
                                }
                              : {}
                          }
                        >
                          {isSpeaking ? (
                            <>
                              <Volume2 className="h-3.5 w-3.5" />
                              Speaking...
                            </>
                          ) : isRecording ? (
                            <>
                              <Radio className="h-3.5 w-3.5 animate-pulse" />
                              Listening...
                            </>
                          ) : (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Thinking...
                            </>
                          )}
                        </Badge>
                      </div>

                      {/* Conversation Area */}
                      <div className="space-y-5">
                        {/* AI Message - Chat Bubble Style */}
                        <div className="flex items-start gap-3">
                          {/* AI Avatar */}
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shadow-sm border border-primary/20">
                              <Sparkles className="h-5 w-5 text-primary" />
                            </div>
                          </div>
                          
                          {/* AI Message Bubble */}
                          <div className="flex-1 min-w-0">
                            <div className="inline-block max-w-[85%]">
                              <div className={cn(
                                "rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm",
                                "bg-primary/5",
                                "border border-primary/20"
                              )}>
                                <div className="flex items-center justify-between gap-2 mb-1.5">
                                  <div className="flex items-center gap-2">
                                    <Text size="xs" weight="semibold" className="text-primary">
                                      AI Assistant
                                    </Text>
                                    <Badge 
                                      variant="outline" 
                                      className="text-[10px] px-1.5 py-0 h-4 border-primary/30 bg-primary/5"
                                    >
                                      Q{currentIndex + 1}
                                    </Badge>
                                  </div>
                                  <Popover open={contextualTipsOpen} onOpenChange={setContextualTipsOpen}>
                                    <PopoverTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-primary hover:bg-primary/10"
                                      >
                                        <Lightbulb className="h-3.5 w-3.5" />
                                        <span className="sr-only">Show tips for this question</span>
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent 
                                      className="w-80 p-3" 
                                      align="end"
                                      side="left"
                                    >
                                      <div className="space-y-2">
                                        <div className="flex items-center gap-2 mb-2">
                                          <Lightbulb className="h-4 w-4 text-primary" />
                                          <Text size="sm" weight="semibold">
                                            Tips for This Question
                                          </Text>
                                        </div>
                                        <div className="space-y-2">
                                          {getContextualTips().map((tip, idx) => {
                                            const Icon = tip.icon;
                                            return (
                                              <div key={idx} className="flex gap-2 text-xs">
                                                <Icon className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5" />
                                                <Text size="xs" variant="muted" className="leading-relaxed">
                                                  {tip.text}
                                                </Text>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                                <Text size="sm" className="leading-relaxed text-foreground">
                                  {questions[currentIndex]}
                                </Text>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* User Response - Chat Bubble Style */}
                        <div className="flex items-start gap-3 justify-end">
                          {/* User Message Bubble */}
                          <div className="flex-1 min-w-0 flex justify-end">
                            <div className="inline-block max-w-[85%]">
                              <div className={cn(
                                "rounded-2xl rounded-tr-sm px-4 py-3 shadow-sm transition-all duration-300",
                                isRecording 
                                  ? "bg-accent/10 border-2 border-accent/40 ring-2 ring-accent/20" 
                                  : transcriptText.current 
                                  ? "bg-accent/5 border border-accent/30"
                                  : "bg-muted/40 border border-border/50",
                              )}>
                                <div className="flex items-center gap-2 mb-1.5">
                                  <Text size="xs" weight="semibold" className={cn(
                                    isRecording ? "text-accent-foreground" : "text-muted-foreground"
                                  )}>
                                    You
                                  </Text>
                                  {isRecording && (
                                    <Badge 
                                      variant="secondary" 
                                      className="text-[10px] px-1.5 py-0 h-4 gap-1 animate-pulse bg-accent/20 border-accent/40"
                                    >
                                      <Radio className="h-2.5 w-2.5" />
                                      Live
                                    </Badge>
                                  )}
                                </div>
                                <Text 
                                  size="sm" 
                                  className={cn(
                                    "leading-relaxed",
                                    transcriptText.current 
                                      ? "text-foreground" 
                                      : "text-muted-foreground italic"
                                  )}
                                >
                                  {transcriptText.current || (isRecording ? "Listening... Speak now" : "Waiting for your response...")}
                                </Text>
                                {isRecording && transcriptText.current && (
                                  <div className="mt-2 flex items-center gap-1.5 pt-1 border-t border-accent/20">
                                    <div className="flex gap-1">
                                      <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></span>
                                      <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" style={{ animationDelay: '0.2s' }}></span>
                                      <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" style={{ animationDelay: '0.4s' }}></span>
                                    </div>
                                    <Text size="xs" variant="muted" className="ml-1">
                                      AI is listening...
                                    </Text>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* User Avatar */}
                          <div className="flex-shrink-0">
                            <div className={cn(
                              "w-10 h-10 rounded-full flex items-center justify-center shadow-sm border transition-all",
                              isRecording 
                                ? "bg-accent/20 border-accent/40 ring-2 ring-accent/30 ring-offset-2 animate-pulse" 
                                : "bg-muted/60 border-border/50"
                            )}>
                              <Mic className={cn(
                                "h-5 w-5",
                                isRecording ? "text-accent animate-pulse" : "text-muted-foreground"
                              )} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Tips Section - Reference Panel Style */}
                  <Collapsible open={tipsOpen} onOpenChange={setTipsOpen}>
                    <Card className="border border-primary/20 bg-background shadow-sm sticky top-4 overflow-hidden">
                      {/* Header */}
                      <CollapsibleTrigger asChild>
                        <CardHeader className={cn(
                          "cursor-pointer transition-all duration-200",
                          "bg-primary/5 hover:bg-primary/10",
                          "border-b border-border"
                        )}>
                          <div className="flex items-center justify-between relative z-10">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-primary/20 shadow-sm ring-1 ring-primary/20">
                                <Lightbulb className="h-5 w-5 text-primary" />
                              </div>
                              <div className="text-left">
                                <CardTitle className="text-base font-semibold text-foreground">
                                  Quick Tips
                                </CardTitle>
                                <CardDescription className="text-xs mt-0.5">
                                  {tipsOpen ? "Click to collapse" : "Click to expand helpful tips"}
                                </CardDescription>
                              </div>
                            </div>
                            <ChevronDown className={cn(
                              "h-4 w-4 text-muted-foreground transition-transform duration-200",
                              tipsOpen && "transform rotate-180"
                            )} />
                          </div>
                        </CardHeader>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <CardContent className="pt-3 pb-3">
                          <div className="space-y-1.5">
                            {[
                              { icon: Volume2, title: "Speak Clearly", text: "Speak naturally and clearly" },
                              { icon: MessageSquare, title: "Be Specific", text: "Use specific titles and experience levels" },
                              { icon: Sparkles, title: "Include Details", text: "Mention salary, location, and key skills" },
                              { icon: CheckCircle2, title: "Keep it Concise", text: "Short, clear phrases work best" },
                              { icon: CheckCircle2, title: "You Can Edit", text: "Review and edit the JD at the end" }
                            ].map((tip, idx) => {
                              const Icon = tip.icon;
                              return (
                                <div 
                                  key={idx} 
                                  className={cn(
                                    "group relative flex gap-2 p-2 rounded-md transition-all duration-200",
                                    "bg-muted/30 border border-border/50 hover:border-primary/30",
                                    "hover:shadow-sm hover:bg-primary/5"
                                  )}
                                >
                                  {/* Icon with background */}
                                  <div className={cn(
                                    "flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center",
                                    "bg-primary/10 border border-primary/20",
                                    "group-hover:bg-primary/15",
                                    "transition-all duration-200"
                                  )}>
                                    <Icon className="h-3.5 w-3.5 text-primary" />
                                  </div>
                                  
                                  {/* Content */}
                                  <div className="flex-1 min-w-0">
                                    <Text 
                                      size="xs" 
                                      weight="semibold" 
                                      className="leading-tight text-foreground group-hover:text-primary transition-colors mb-0.5"
                                    >
                                      {tip.title}
                                    </Text>
                                    <Text 
                                      size="xs" 
                                      variant="muted" 
                                      className="leading-tight"
                                    >
                                      {tip.text}
                                    </Text>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                </div>
        </div>
      </div>
    </EmployerLayout>
  );
}
